/**
 * TeamMembersPage — 系统设置「团队成员」二级页（从 SettingsPage 巨石拆出，纯搬移）。
 * 读真：root 专属 GET /admin-users + GET /auth/invites；写=邀请/编辑/移除/重发邀请/导出 CSV。
 * 弹窗（邀请成员 / 成员编辑）由本页自管 open state，不再经父壳回调。
 */

import type { AdminAccountResponse } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAdminUsers, usePendingInvites } from '~/data/queries';
import { adminUsersApi } from '~/hooks/use-admin-users';
import { resendInvite } from '~/hooks/use-auth';
import { fmtDate, fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Avatar } from '~/ui/Avatar';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { SearchInput } from '~/ui/SearchInput';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { InviteMemberDialog } from './dialogs/InviteMemberDialog';
import { MemberEditDialog } from './dialogs/MemberEditDialog';
import { SectionHead } from './settings-shared';

/** 4 角色闭集中文标签 + chip 配色 */
const ROLE_META: Record<AdminAccountResponse['role'], { label: string; chip: string }> = {
  root: { label: 'Root', chip: 'chip-accent' },
  admin: { label: 'Admin', chip: 'chip-info' },
  operator: { label: '运营', chip: '' },
  viewer: { label: '只读', chip: '' },
};

/** 本页自管弹窗 open state（邀请成员 / 成员编辑）*/
type ActiveDialog =
  | { kind: 'invite-member' }
  | { kind: 'member-edit'; member: AdminAccountResponse }
  | null;

export default function TeamMembersPage() {
  const [dialog, setDialog] = useState<ActiveDialog>(null);
  // 团队成员读真：root 专属端点（GET /admin-users）→ 非 root 会话 enabled=false 返空集（不读 root 残留缓存）
  const role = useAuthStore((s) => s.user?.role);
  const canManageMembers = role === 'root'; // 编辑 / 移除 → 后端 requireRole('root')
  const canInvite = role === 'root' || role === 'admin'; // 邀请 → requireRole('root','admin')
  const { data: members, isLoading } = useAdminUsers(canManageMembers);
  // 待接受邀请（GET /auth/invites，root/admin）：邀请落 admin_invites，接受后才迁入成员列表。
  const { data: pendingInvites, isLoading: invitesLoading } = usePendingInvites(canInvite);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<{ member: AdminAccountResponse } | null>(null);

  // 重发邀请：轮转 token + 刷新 24h + 重发邮件 → invalidate 刷新过期态
  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: async ({ email }) => {
      toast(`已重发邀请至 ${email}`, { tone: 'pos', icon: 'IconMail' });
      await queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '重发失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => adminUsersApi.remove(id),
    onSuccess: async (_res, id) => {
      const removed = members.find((m) => m.id === id);
      toast(`已移除成员 ${removed?.nickname ?? ''}`, { tone: 'warn', icon: 'IconTrash' });
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '移除失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 成员列表 CSV 导出（root 专属，裸 CSV 流）：truncated 透出提示「仅导部分」
  const exportMutation = useMutation({
    mutationFn: () => adminUsersApi.exportCsv(),
    onSuccess: ({ truncated }) =>
      toast(truncated ? '已导出（超上限仅导最新部分）' : '成员列表已导出', {
        tone: truncated ? 'warn' : 'pos',
        icon: 'IconDownload',
      }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '导出失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 客户端过滤：搜索匹配 nickname/email；角色多选
  const filtered = members.filter((m) => {
    if (
      q &&
      !(
        m.nickname.toLowerCase().includes(q.toLowerCase()) ||
        m.email.toLowerCase().includes(q.toLowerCase()) ||
        (m.account_code ?? '').toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    if (roleFilter.length && !roleFilter.includes(m.role)) return false;
    return true;
  });

  // page-enter 淡入（对照原型数据到达后的内容浮现）；本 wrapper 自起 flex 列复刻父级 gap:14，
  // 让真实内容既保持原间距又能挂上 fade-in 动画（display:contents 会丢失盒子→动画失效，故不用）。
  // 不整页骨架早退：页头（含邀请按钮）+ 搜索/筛选/导出 chrome 始终渲染（各自独立 state，不依赖 useAdminUsers），
  // 仅在依赖该查询的成员表区域内显骨架（TableSkeleton），加载中操作员仍能邀请成员、用搜索/筛选。
  // 门控由真 TanStack Query isLoading 驱动；非 root 会话 enabled=false → 查询 idle，isLoading=false，不卡骨架。
  return (
    <div className="page-enter vstack" style={{ gap: 14 }}>
      <SectionHead
        title="团队成员"
        sub={
          canManageMembers
            ? `${members.length} 位运营成员`
            : '团队成员管理仅超管可查看（后端 root 专属）'
        }
        action={
          <button
            className="btn btn-accent"
            onClick={() => setDialog({ kind: 'invite-member' })}
            disabled={!canInvite}
            title={canInvite ? undefined : '仅 root / admin 可邀请成员'}
          >
            <Icons.IconPlus size={13} />
            邀请成员
          </button>
        }
      />
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput
          width={240}
          placeholder="搜索成员…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <FilterButton
          label="角色"
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: 'root', label: 'Root' },
            { value: 'admin', label: 'Admin' },
            { value: 'operator', label: '运营' },
            { value: 'viewer', label: '只读' },
          ]}
          width={160}
        />
        <div style={{ flex: 1 }} />
        {/* 导出成员列表 CSV：GET /admin-users/export（root 专属，裸 CSV 流）→ downloadAuthedCsv */}
        <button
          className="btn"
          disabled={!canManageMembers || exportMutation.isPending}
          title={canManageMembers ? undefined : '团队成员导出仅超管可用（后端 root 专属）'}
          style={canManageMembers ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
          onClick={() => exportMutation.mutate()}
        >
          <Icons.IconDownload size={12} />
          {exportMutation.isPending ? '导出中…' : '导出'}
        </button>
      </div>
      {/* 成员表区域：仅这块依赖 useAdminUsers → 加载中显 TableSkeleton（同形 6 列无 checkbox），
          上方页头/邀请/搜索/筛选/导出已先渲染，不被本查询阻塞 */}
      {isLoading ? (
        <TableSkeleton rows={8} cols={6} checkbox={false} />
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th>成员</th>
                <th>角色</th>
                <th>状态</th>
                <th>最近登录</th>
                <th>双因子</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="hstack" style={{ gap: 10 }}>
                      <Avatar hue={(m.nickname.charCodeAt(0) * 39) % 360} size={30} fontSize={12}>
                        {m.nickname[0]?.toUpperCase()}
                      </Avatar>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 12.5 }}>{m.nickname}</div>
                        <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                          {m.email}
                          <span style={{ marginLeft: 8 }}>CODE: {m.account_code ?? '未设置'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`chip ${ROLE_META[m.role].chip}`} style={{ fontSize: 10 }}>
                      {ROLE_META[m.role].label}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`chip ${m.status === 'active' ? 'chip-pos' : m.status === 'pending' ? 'chip-warn' : ''}`}
                      style={{ fontSize: 10 }}
                    >
                      {m.status === 'active'
                        ? '已激活'
                        : m.status === 'pending'
                          ? '邀请中'
                          : '已停用'}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {m.last_login_at ? fmtDate(m.last_login_at) : '—'}
                  </td>
                  <td>
                    {m.totp_enabled ? (
                      <span className="chip chip-pos" style={{ fontSize: 10 }}>
                        <Icons.IconShield size={9} />
                        已启用
                      </span>
                    ) : (
                      <span className="chip chip-warn" style={{ fontSize: 10 }}>
                        未启用
                      </span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="hstack" style={{ gap: 4 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        title={canManageMembers ? '编辑成员' : '仅 root 可管理运营账号'}
                        disabled={!canManageMembers}
                        onClick={() => setDialog({ kind: 'member-edit', member: m })}
                      >
                        <Icons.IconEdit size={11} />
                      </button>
                      <ContextMenu
                        items={[
                          {
                            label: '编辑角色 / 状态',
                            icon: 'IconEdit',
                            disabled: !canManageMembers,
                            title: canManageMembers ? undefined : '仅 root 可管理运营账号',
                            onClick: () => setDialog({ kind: 'member-edit', member: m }),
                          },
                          {
                            label: '复制邮箱',
                            icon: 'IconCopy',
                            onClick: () => {
                              navigator.clipboard?.writeText(m.email);
                              toast('邮箱已复制', { tone: 'pos', icon: 'IconCheck' });
                            },
                          },
                          // 注：重发邀请已迁至下方「待接受邀请」分区（邀请落 admin_invites，非成员行）
                          '---',
                          {
                            label:
                              m.role === 'root'
                                ? '移除成员（root 不可移除）'
                                : !canManageMembers
                                  ? '移除成员（仅 root）'
                                  : '移除成员',
                            icon: 'IconTrash',
                            tone: 'danger',
                            disabled: !canManageMembers || m.role === 'root',
                            title:
                              m.role === 'root'
                                ? 'root 账号不可移除'
                                : canManageMembers
                                  ? undefined
                                  : '仅 root 可移除运营账号',
                            onClick: () => setConfirm({ member: m }),
                          },
                        ]}
                      >
                        <button className="btn btn-ghost btn-sm">
                          <Icons.IconMoreV size={11} />
                        </button>
                      </ContextMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {filtered.length === 0 && (
            <div
              style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}
            >
              {/* 本分支为非加载态（isLoading 时显 TableSkeleton）→ 仅区分「无权查看」与「暂无成员」 */}
              {!canManageMembers ? '团队成员管理仅超管可查看' : '暂无成员'}
            </div>
          )}
        </div>
      )}
      {/* 待接受邀请：surface admin_invites（未消费）；接受后才迁入上方成员列表。root/admin 可见 + 重发。 */}
      {canInvite && (
        <div className="vstack" style={{ gap: 10, marginTop: 6 }}>
          <SectionHead
            title="待接受邀请"
            sub={
              invitesLoading
                ? '加载中…'
                : pendingInvites.length === 0
                  ? '暂无待接受邀请'
                  : `${pendingInvites.length} 个邀请待接受（接受后迁入团队成员）`
            }
          />
          {invitesLoading ? (
            <TableSkeleton rows={2} cols={5} checkbox={false} />
          ) : pendingInvites.length > 0 ? (
            <div className="card" style={{ overflow: 'hidden' }}>
              <Table className="tbl">
                <thead>
                  <tr>
                    <th>受邀邮箱</th>
                    <th>角色</th>
                    <th>邀请人</th>
                    <th>状态</th>
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv) => (
                    <tr key={inv.id}>
                      <td className="mono" style={{ fontSize: 11.5 }}>
                        {inv.email}
                      </td>
                      <td>
                        <span
                          className={`chip ${ROLE_META[inv.role].chip}`}
                          style={{ fontSize: 10 }}
                        >
                          {ROLE_META[inv.role].label}
                        </span>
                      </td>
                      <td style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                        {inv.invited_by_nickname ?? '—'}
                      </td>
                      <td>
                        {inv.is_expired ? (
                          <span className="chip chip-neg" style={{ fontSize: 10 }}>
                            已过期
                          </span>
                        ) : (
                          <span className="chip chip-warn" style={{ fontSize: 10 }}>
                            待接受 · {fmtDateTime(inv.expires_at)} 前
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={resendMutation.isPending}
                          title="重发邀请邮件（轮转链接 + 刷新 24h 有效期）"
                          onClick={() => resendMutation.mutate(inv.id)}
                        >
                          <Icons.IconMail size={11} />
                          重发
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={`移除成员 ${confirm.member.nickname}？`}
          message={`移除后 ${confirm.member.email} 将立即失去后台访问权限，已建立的会话会被终止。`}
          tone="danger"
          confirmLabel="确认移除"
          onConfirm={() => removeMutation.mutate(confirm.member.id)}
          onClose={() => setConfirm(null)}
        />
      )}
      {dialog?.kind === 'invite-member' && <InviteMemberDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'member-edit' && (
        <MemberEditDialog member={dialog.member} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
